import React from 'react';
import PropTypes from 'prop-types';
import './Card.css';

const Card = ({
	title,
	buttons,
	children,
	content,
	bgColor = 'white',
	type = 'default',
	className = '',
	headerClassName = '',
	bodyClassName = '',
	footerClassName = '',
	...props
}) => {
	// Determine card type classes and styles
	const getCardClasses = () => {
		let cardClass = '';
		let textClass = '';
		let customStyle = {};

		// Type prop takes precedence over bgColor
		if (type && type !== 'default') {
			switch (type) {
				case 'primary':
					cardClass = 'text-bg-primary';
					textClass = 'text-white';
					break;
			case 'secondary':
				// Custom secondary color as requested
				cardClass = 'card-secondary';
				break;
				case 'success':
					cardClass = 'text-bg-success';
					textClass = 'text-white';
					break;
				case 'danger':
					cardClass = 'text-bg-danger';
					textClass = 'text-white';
					break;
				case 'warning':
					cardClass = 'text-bg-warning';
					break;
				case 'info':
					cardClass = 'text-bg-info';
					textClass = 'text-white';
					break;
				case 'light':
					cardClass = 'text-bg-light';
					break;
				case 'dark':
					cardClass = 'text-bg-dark';
					textClass = 'text-white';
					break;
				default:
					cardClass = 'bg-white';
			}
		} else {
			// Fallback to bgColor prop logic
			if (bgColor === 'gray' || bgColor === 'grey') {
				cardClass = 'bg-light';
			} else if (bgColor === 'white' || bgColor === 'default') {
				cardClass = 'bg-white';
			} else if (bgColor.startsWith('bg-')) {
				cardClass = bgColor;
			} else if (bgColor !== 'white' && bgColor !== 'default') {
				// Custom color - will be applied as inline style
				customStyle = { backgroundColor: bgColor };
			} else {
				cardClass = 'bg-white';
			}
		}

		return { cardClass, textClass, customStyle };
	};

	const { cardClass, textClass, customStyle } = getCardClasses();

	return (
		<div
			className={`card v3-card v3-scope ${cardClass} ${className}`.trim()}
			style={customStyle}
			{...props}
		>
			{/* Card Header - Only render if title or buttons are provided */}
			{(title || buttons) && (
				<div className={`card-header ${textClass} ${headerClassName}`.trim()}>
					<div className="d-flex justify-content-between align-items-center">
						{/* Title Section */}
						{title && (
							<h4 className="card-title mb-0">
								{typeof title === 'string' ? title : title}
							</h4>
						)}

						{/* Buttons Section */}
						{buttons && (
							<div className="card-header-buttons">
								{Array.isArray(buttons) ? (
									<div className="btn-group btn-group-sm" role="group">
										{buttons.map((button, index) => (
											<React.Fragment key={index}>{button}</React.Fragment>
										))}
									</div>
								) : (
									buttons
								)}
							</div>
						)}
					</div>
				</div>
			)}

			{/* Card Body */}
			<div className={`card-body ${bodyClassName}`.trim()}>
				{/* Content prop takes precedence over children */}
				{content || children}
			</div>

			{/* Card Footer - Only render if explicitly provided via children with card-footer class */}
			{/* This allows for flexible footer content when needed */}
		</div>
	);
};

Card.propTypes = {
	/** Card title - can be string or React element */
	title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	/** Buttons to display in card header - can be single button or array of buttons */
	buttons: PropTypes.oneOfType([
		PropTypes.element,
		PropTypes.arrayOf(PropTypes.element),
	]),
	/** Main content of the card - takes precedence over children */
	content: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.element,
		PropTypes.node,
	]),
	/** Background color - 'gray'/'grey' for light gray, 'white' for white, or any valid CSS color/Bootstrap bg- class */
	bgColor: PropTypes.string,
	/** Additional CSS classes for the card container */
	className: PropTypes.string,
	/** Additional CSS classes for the card header */
	headerClassName: PropTypes.string,
	/** Additional CSS classes for the card body */
	bodyClassName: PropTypes.string,
	/** Additional CSS classes for the card footer (if used) */
	footerClassName: PropTypes.string,
	/** Child elements - used if content prop is not provided */
	children: PropTypes.node,

	/** Card type - determines the visual appearance and color scheme */
	type: PropTypes.oneOf([
		'default',
		'primary',
		'secondary',
		'success',
		'danger',
		'warning',
		'info',
		'light',
		'dark',
	]),
};

export default Card;
